import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Menu, X, User } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Toaster, toast } from 'react-hot-toast';
import { trackPageView } from './utils/analytics';
import HomePage from './pages/HomePage';
import AdminPage from './pages/AdminPage';
import EntreprisePage from './pages/EntreprisePage';
import AboutPage from './pages/AboutPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import { SharingOperationsPage } from './pages/SharingOperationsPage';
import { AcceptInvitationPage } from './pages/AcceptInvitationPage';
import QuestionnairePage from './pages/QuestionnairePage';
import NousRejoindre from './pages/NousRejoindre';
import NosRealisationsPage from './pages/NosRealisationsPage';
import ContactPage from './pages/ContactPage';
import { ContactModal } from './components/ContactModal';
import { LoginModal } from './components/LoginModal';
import { MemberDashboard } from './components/MemberDashboard';
import { AdminDashboard } from './components/AdminDashboard';
import { GuestSimulatorDashboard } from './components/GuestSimulatorDashboard';
import { ServiceSelectionModal, ServiceType } from './components/ServiceSelectionModal';
import { LanguageSelector } from './components/LanguageSelector';
import { useAutoLogout } from './hooks/useAutoLogout';
import { supabase, isSupabaseConfigured } from './lib/supabase';

const isQuestionnairePage = () =>
  typeof window !== 'undefined' && window.location.pathname.startsWith('/questionnaire');

function SupabaseConnectionBanner() {
  const [showBanner, setShowBanner] = useState(!isSupabaseConfigured);
  if (!showBanner) return null;
  return (
    <div className="bg-brand-flame/10 border-b border-brand-flame px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-2 h-2 bg-brand-flame/10 rounded-full animate-pulse"></div>
          <p className="text-sm text-brand-flame">
            <strong>Database not connected:</strong> Please configure Supabase environment variables for full functionality
          </p>
        </div>
        <button
          onClick={() => setShowBanner(false)}
          className="text-brand-flame hover:text-brand-flame text-sm underline"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

function NavigationTabs() {
  const location = useLocation();
  const { t } = useTranslation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const pageTitle = {
      '/': 'Accueil | Sun Is Up - Communauté d\'Énergie Bruxelles',
      '/about': 'À Propos | Sun Is Up - Thomas et Simon, Ingénieurs',
      '/simulation': 'Simulation Gratuite | Sun Is Up - Calculez vos Économies',
      '/admin': 'Rejoindre | Sun Is Up - Devenez Membre'
    }[location.pathname] || 'Sun Is Up';

    trackPageView(location.pathname, pageTitle);
  }, [location.pathname]);

  return (
    <nav className="bg-transparent sticky top-0 z-40">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Desktop Navigation */}
          <div className="hidden md:flex space-x-8">
            <Link
              to="/"
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === '/' 
                  ? 'text-black border-b-2 border-black'
                  : 'text-black hover:text-neutral-800'
              }`}
            >
              {t('nav.home')}
            </Link>
            <Link
              to="/about"
              className={`px-3 py-2 text-sm font-medium transition-colors ${
                location.pathname === '/about'
                  ? 'text-black border-b-2 border-black'
                  : 'text-black hover:text-neutral-800'
              }`}
            >
              {t('nav.about')}
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex-shrink-0">
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="text-black hover:text-neutral-800 p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-neutral-300 py-2 bg-white/90">
            <div className="flex flex-col space-y-2">
              <Link
                to="/"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${
                  location.pathname === '/' 
                    ? 'text-black bg-neutral-50'
                    : 'text-black hover:text-neutral-800'
                }`}
              >
                {t('nav.home')}
              </Link>
              <Link
                to="/about"
                onClick={() => setMobileMenuOpen(false)}
                className={`block px-4 py-3 text-sm font-medium ${
                  location.pathname === '/about'
                    ? 'text-black bg-neutral-50'
                    : 'text-black hover:text-neutral-800'
                }`}
              >
                {t('nav.about')}
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}

function MainApp() {
  const [showContactModal, setShowContactModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState('fr');
  const [user, setUser] = useState<any>(null);
  const [showDashboard, setShowDashboard] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [isSimulatorGuest, setIsSimulatorGuest] = useState(false);
  const [simulatorGuestInvitation, setSimulatorGuestInvitation] = useState<any>(null);
  const [adminOperationId, setAdminOperationId] = useState<string | null>(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [segment, setSegment] = useState<'entreprises' | 'particuliers'>('entreprises');
  const [availableServices, setAvailableServices] = useState<ServiceType[]>([]);
  const [activeService, setActiveService] = useState<ServiceType | null>(null);
  const [showServiceSelection, setShowServiceSelection] = useState(false);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLanguageChange = (language: string) => {
    setCurrentLanguage(language);
    i18n.changeLanguage(language);
  };

  const handleLoginSuccess = async (userData: any) => {
    // Verifier si c'est le superadmin
    if (userData.email === 'info@sunisup.be') {
      setUser(userData);
      setShowLoginModal(false);
      setIsAdmin(true);
      setIsSuperAdmin(true);
      setAdminOperationId(null);
      toast.success(t('login.success'));
      return;
    }

    // Verifier si c'est un admin d'operation
    try {
      const { data: adminOp, error: adminError } = await supabase
        .from('sharing_operation_admins')
        .select('*')
        .ilike('email', userData.email)
        .not('invitation_accepted_at', 'is', null)
        .maybeSingle();

      if (!adminError && adminOp) {
        setUser(userData);
        setShowLoginModal(false);
        setIsAdmin(true);
        setIsSuperAdmin(false);
        setAdminOperationId(adminOp.sharing_operation_id);
        toast.success(t('login.success'));
        return;
      }

      // Verifier si c'est un invité simulateur
      let { data: simInvitation } = await supabase
        .from('simulator_invitations')
        .select('*')
        .eq('user_id', userData.id)
        .maybeSingle();

      if (!simInvitation) {
        const { data: simByEmail } = await supabase
          .from('simulator_invitations')
          .select('*')
          .eq('email', userData.email)
          .maybeSingle();
        if (simByEmail) {
          if (simByEmail.user_id !== userData.id) {
            await supabase.rpc('link_simulator_invitation_user', {
              p_email: userData.email,
              p_user_id: userData.id,
              p_duration_days: null,
            });
          }
          simInvitation = { ...simByEmail, user_id: userData.id };
        }
      }

      if (simInvitation) {
        if (!simInvitation.validated_at) {
          toast.error('Votre compte est en attente de validation par notre équipe. Vous pourrez vous connecter dès validation.');
          await supabase.auth.signOut();
          return;
        }
        if (simInvitation.expires_at && new Date(simInvitation.expires_at) < new Date()) {
          toast.error('Votre accès au simulateur a expiré. Contactez l\'administrateur pour renouveler votre accès.');
          await supabase.auth.signOut();
          return;
        }

        // Check if user is also an operation admin (dual-role: admin + simulator)
        const { data: adminOpForSim } = await supabase
          .from('sharing_operation_admins')
          .select('*')
          .ilike('email', userData.email)
          .not('invitation_accepted_at', 'is', null)
          .maybeSingle();

        if (adminOpForSim) {
          setUser(userData);
          setShowLoginModal(false);
          setIsAdmin(true);
          setIsSuperAdmin(false);
          setIsSimulatorGuest(true);
          setSimulatorGuestInvitation(simInvitation);
          setAdminOperationId(adminOpForSim.sharing_operation_id);
          setShowDashboard(true);
          setAvailableServices(['admin', 'simulator']);
          setActiveService(null);
          setShowServiceSelection(true);
          toast.success(t('login.success'));
          return;
        }

        // Check if user is also a participant member (dual-role: member + simulator)
        const { data: accountsForSim } = await supabase
          .from('participant_accounts')
          .select('account_status')
          .eq('email', userData.email.toLowerCase())
          .eq('account_status', 'approved')
          .limit(1);
        const isMemberToo = (accountsForSim?.length ?? 0) > 0;

        setUser(userData);
        setShowLoginModal(false);
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setIsSimulatorGuest(true);
        setSimulatorGuestInvitation(simInvitation);
        setAdminOperationId(null);
        setShowDashboard(true);
        toast.success(t('login.success'));

        if (isMemberToo) {
          setAvailableServices(['member', 'simulator']);
          setActiveService(null);
          setShowServiceSelection(true);
        } else {
          setAvailableServices(['simulator']);
          setActiveService('simulator');
        }
        return;
      }

      // Member access is granted only through a validated participant_accounts row.
      const { data: accountRows, error: accountError } = await supabase
        .from('participant_accounts')
        .select('account_status')
        .eq('email', userData.email.toLowerCase())
        .order('account_request_date', { ascending: false });

      if (accountError) {
        console.error('Error checking participant_accounts:', accountError);
      }

      const accountStatus = accountRows?.some(r => r.account_status === 'approved')
        ? 'approved'
        : accountRows?.[0]?.account_status;

      if (!accountStatus) {
        toast.error('Aucun compte membre trouve pour cet email. Veuillez creer un compte avec votre code EAN.');
        await supabase.auth.signOut();
        return;
      }

      if (accountStatus !== 'approved') {
        if (accountStatus === 'rejected') {
          toast.error('Votre demande de compte a ete refusee. Contactez-nous pour plus d\'informations.');
        } else {
          toast.error('Votre compte est en attente de validation par notre equipe. Vous recevrez un email de confirmation.');
        }
        await supabase.auth.signOut();
        return;
      }

      setUser(userData);
      setShowLoginModal(false);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSimulatorGuest(false);
      setSimulatorGuestInvitation(null);
      setAdminOperationId(null);
      setAvailableServices(['member']);
      setActiveService('member');
      toast.success(t('login.success'));
    } catch (error) {
      console.error('Error in login flow:', error);
      toast.error('Une erreur est survenue lors de la connexion. Veuillez reessayer.');
      await supabase.auth.signOut();
    }
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      sessionStorage.setItem('explicit_logout', '1');
      await supabase.auth.signOut();
      setUser(null);
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsSimulatorGuest(false);
      setSimulatorGuestInvitation(null);
      setAdminOperationId(null);
      setShowDashboard(false);
      setAvailableServices([]);
      setActiveService(null);
      setShowServiceSelection(false);
      toast.success(t('logout.success'));
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('logout.error'));
    } finally {
      setIsLoggingOut(false);
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      if (sessionStorage.getItem('explicit_logout') === '1') {
        sessionStorage.removeItem('explicit_logout');
        await supabase.auth.signOut();
        return;
      }
      const { data: { user: currentUser } } = await supabase.auth.getUser();
      if (currentUser) {
        await handleLoginSuccess(currentUser);
      }
    };
    checkAuth();
  }, []);

  const handleServiceSelect = (service: ServiceType) => {
    setActiveService(service);
    setShowServiceSelection(false);
    if (service === 'admin' && isAdmin && adminOperationId) {
      localStorage.setItem('selected_sharing_operation_id', adminOperationId);
      localStorage.setItem('dual_role_services', JSON.stringify(availableServices));
      window.location.href = '/admin';
    } else {
      localStorage.removeItem('dual_role_services');
    }
  };

  if (showDashboard && user) {
    if (isSuperAdmin) {
      window.location.href = '/admin/operations';
      return null;
    }

    // Single-role admin (no simulator access): redirect directly
    if (isAdmin && adminOperationId && !isSimulatorGuest) {
      localStorage.setItem('selected_sharing_operation_id', adminOperationId);
      window.location.href = '/admin';
      return null;
    }

    if (showServiceSelection) {
      return (
        <ServiceSelectionModal
          availableServices={availableServices}
          onSelect={handleServiceSelect}
        />
      );
    }

    if (activeService === 'simulator' && isSimulatorGuest && simulatorGuestInvitation) {
      return (
        <GuestSimulatorDashboard
          user={user}
          invitation={simulatorGuestInvitation}
          onLogout={handleLogout}
          availableServices={availableServices}
          onSwitchService={() => setShowServiceSelection(true)}
        />
      );
    }

    return (
      <MemberDashboard
        user={user}
        onLogout={handleLogout}
        availableServices={availableServices}
        onSwitchService={availableServices.length > 1 ? () => setShowServiceSelection(true) : undefined}
      />
    );
  }

  return (
    <Router>
      <AppContent
        showContactModal={showContactModal}
        setShowContactModal={setShowContactModal}
        showLoginModal={showLoginModal}
        setShowLoginModal={setShowLoginModal}
        currentLanguage={currentLanguage}
        handleLanguageChange={handleLanguageChange}
        user={user}
        setShowDashboard={setShowDashboard}
        isLoggingOut={isLoggingOut}
        isScrolled={isScrolled}
        handleLoginSuccess={handleLoginSuccess}
        segment={segment}
        setSegment={setSegment}
        t={t}
      />
    </Router>
  );
}

function App() {
  if (isQuestionnairePage()) {
    return (
      <Router>
        <Routes>
          <Route path="/questionnaire" element={<QuestionnairePage />} />
        </Routes>
      </Router>
    );
  }
  return <MainApp />;
}

function AppContent({
  showContactModal,
  setShowContactModal,
  showLoginModal,
  setShowLoginModal,
  currentLanguage,
  handleLanguageChange,
  user,
  setShowDashboard,
  isLoggingOut,
  isScrolled,
  handleLoginSuccess,
  segment,
  setSegment,
  t
}: {
  showContactModal: boolean;
  setShowContactModal: (v: boolean) => void;
  showLoginModal: boolean;
  setShowLoginModal: (v: boolean) => void;
  currentLanguage: string;
  handleLanguageChange: (lang: string) => void;
  user: any;
  setShowDashboard: (v: boolean) => void;
  isLoggingOut: boolean;
  isScrolled: boolean;
  handleLoginSuccess: (userData: any) => void;
  segment: 'entreprises' | 'particuliers';
  setSegment: (v: 'entreprises' | 'particuliers') => void;
  t: (key: string) => string;
}) {
  const location = useLocation();
  const navigate = useNavigate();
  const isAdminRoute = location.pathname.startsWith('/admin') || location.pathname.startsWith('/simulator');

  const handleSegmentChange = (newSegment: 'entreprises' | 'particuliers') => {
    setSegment(newSegment);
    if (location.pathname !== '/') {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen w-screen overflow-x-hidden bg-white font-sans">
      <SupabaseConnectionBanner />

      {!isAdminRoute && (
        <>
          {/* White Header */}
          <header
            className={`fixed top-0 left-0 right-0 z-50 font-sans text-black transition-all duration-300 ${
              isScrolled ? 'shadow-lg' : 'shadow-none'
            }`}
            style={{ backgroundColor: isScrolled ? '#FFF5E1' : 'transparent' }}
          >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-28 md:h-[92px]">
              <Link to="/" className="flex items-center space-x-3">
                <img src="/images/logosite.png" alt="Sun Is Up" className="object-contain w-28 h-28 md:w-[210px] md:h-[210px] md:relative md:top-[25%] md:right-[20%]" />
              </Link>

              <div className="hidden md:flex items-center whitespace-nowrap">
                <div className="inline-flex rounded-full p-1 relative" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                  <button
                    onClick={() => handleSegmentChange('entreprises')}
                    className={`px-[26px] py-[9px] rounded-full font-semibold transition-all duration-300 text-[15.4px] ${
                      segment === 'entreprises'
                        ? 'shadow-md'
                        : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                    style={
                      segment === 'entreprises'
                        ? { backgroundColor: '#F8BD3D', color: '#1a1a1a' }
                        : { backgroundColor: 'transparent' }
                    }
                  >
                    {t('nav.businesses')}
                  </button>
                  <button
                    onClick={() => handleSegmentChange('particuliers')}
                    className={`px-[26px] py-[9px] rounded-full font-semibold transition-all duration-300 text-[15.4px] relative ${
                      segment === 'particuliers'
                        ? 'shadow-md'
                        : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                    style={
                      segment === 'particuliers'
                        ? { backgroundColor: '#F8BD3D', color: '#1a1a1a' }
                        : { backgroundColor: 'transparent' }
                    }
                  >
                    {t('nav.individuals')}
                  </button>
                </div>
                <Link
                  to="/about"
                  className="ml-8 px-[18px] py-[9px] text-[15.4px] font-semibold transition-colors text-black hover:text-neutral-700 whitespace-nowrap"
                >
                  {t('nav.about')}
                </Link>
                <Link
                  to="/nos-realisations"
                  className="ml-2 px-[18px] py-[9px] text-[15.4px] font-semibold transition-colors text-black hover:text-neutral-700 whitespace-nowrap"
                >
                  Nos réalisations
                </Link>
              </div>

              <div className="flex items-center space-x-2">
                <div className="hidden md:block">
                  <LanguageSelector
                    currentLanguage={currentLanguage}
                    onLanguageChange={handleLanguageChange}
                  />
                </div>

                {user ? (
                  <button
                    onClick={() => setShowDashboard(true)}
                    disabled={isLoggingOut}
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-[18px] py-[9px] rounded-lg font-medium transition-colors flex items-center gap-2 text-[15.4px] font-sans"
                  >
                    <User style={{ width: '20px', height: '20px' }} />
                    <span className="hidden sm:inline">Dashboard</span>
                  </button>
                ) : (
                  <button
                    onClick={() => setShowLoginModal(true)}
                    className="bg-brand-flame hover:bg-brand-flame-light text-black px-[9.6px] py-[9px] rounded-lg font-medium transition-colors flex items-center gap-2 text-[15.4px] font-sans whitespace-nowrap"
                  >
                    <User style={{ width: '20px', height: '20px' }} />
                    <span className="hidden sm:inline">{t('header.memberAccess')}</span>
                  </button>
                )}

                <Link
                  to="/contact"
                  className="hidden md:flex bg-brand-gold hover:bg-brand-gold-light text-black px-[12.8px] py-[9px] rounded-lg font-medium transition-colors items-center gap-2 text-[15.4px] font-sans whitespace-nowrap"
                >
                  <span>{t('header.contact')}</span>
                  <ArrowRight style={{ width: '20px', height: '20px' }} />
                </Link>

                <MobileNavigation currentLanguage={currentLanguage} handleLanguageChange={handleLanguageChange} />
              </div>
            </div>
          </div>

          {/* Mobile Segment Selector - Visible only on mobile, below header */}
          {location.pathname === '/' && (
            <div className="md:hidden py-3 border-t" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <div className="max-w-7xl mx-auto px-4">
                <div className="inline-flex rounded-full p-1 w-full" style={{ backgroundColor: 'rgba(0, 0, 0, 0.08)' }}>
                  <button
                    onClick={() => handleSegmentChange('entreprises')}
                    className={`flex-1 px-4 py-2 rounded-full font-semibold transition-all duration-300 text-xs ${
                      segment === 'entreprises'
                        ? 'shadow-md'
                        : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                    style={
                      segment === 'entreprises'
                        ? { backgroundColor: '#F8BD3D', color: '#1a1a1a' }
                        : { backgroundColor: 'transparent' }
                    }
                  >
                    {t('nav.businesses')}
                  </button>
                  <button
                    onClick={() => handleSegmentChange('particuliers')}
                    className={`flex-1 px-4 py-2 rounded-full font-semibold transition-all duration-300 text-xs relative ${
                      segment === 'particuliers'
                        ? 'shadow-md'
                        : 'text-neutral-600 hover:text-neutral-800'
                    }`}
                    style={
                      segment === 'particuliers'
                        ? { backgroundColor: '#F8BD3D', color: '#1a1a1a' }
                        : { backgroundColor: 'transparent' }
                    }
                  >
                    {t('nav.individuals')}
                  </button>
                </div>
              </div>
            </div>
          )}
        </header>

        </>
      )}

      <main>
          <Routes>
            <Route path="/" element={<HomePage segment={segment} />} />
            <Route path="/quickscan" element={<HomePage segment="entreprises" autoOpenSimulator={true} />} />
            <Route path="/particuliers" element={<HomePage segment="particuliers" />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/nos-realisations" element={<NosRealisationsPage />} />
            <Route path="/nous-rejoindre" element={<NousRejoindre />} />
            <Route path="/simulation" element={<EntreprisePage />} />
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/operations" element={<SharingOperationsPage />} />
            <Route path="/admin/accept-invitation" element={<AcceptInvitationPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/contact" element={<ContactPage />} />
          </Routes>
        </main>

      {!isAdminRoute && (
        <footer style={{ backgroundColor: '#FFF5E1' }} className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="flex items-center space-x-3 mb-6">
                  <img src="/images/Logosite2.png" alt="Sun Is Up" className="w-12 h-12" />
                  <div>
                    <h3 className="text-xl font-bold text-neutral-800 font-sans">Sun Is Up</h3>
                    <p className="text-neutral-700 text-sm font-sans">{t('footer.description')}</p>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-neutral-800 mb-4 font-sans">{t('footer.contact')}</h4>
                <div className="space-y-2 text-neutral-700">
                  <p className="font-sans">info@sunisup.be</p>
                  <p className="font-sans">Bruxelles, Belgique</p>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold text-neutral-800 mb-4 font-sans">{t('footer.followUs')}</h4>
                <div className="flex space-x-4">
                  <a href="https://www.linkedin.com/company/sun-is-up-asbl/" target="_blank" rel="noopener noreferrer"
                     className="text-neutral-700 hover:text-neutral-900 transition-colors">
                    LinkedIn
                  </a>
                </div>
              </div>
            </div>

            <div className="border-t mt-12 pt-8 text-center" style={{ borderColor: 'rgba(0, 0, 0, 0.1)' }}>
              <p className="text-neutral-600 font-sans">{t('footer.copyright')}</p>
            </div>
          </div>
        </footer>
      )}

      <Toaster position="top-right" toastOptions={{ duration: 8000, success: { duration: 4000 }, error: { duration: 8000 } }} />

      <ContactModal isOpen={showContactModal} onClose={() => setShowContactModal(false)} />
      <LoginModal isOpen={showLoginModal} onClose={() => setShowLoginModal(false)} onLoginSuccess={handleLoginSuccess} />
    </div>
  );
}

function NavigationLinks() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = React.useState<'home' | 'services' | 'about'>('home');

  const scrollToHome = () => {
    if (location.pathname === '/') {
      setActiveSection('home');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate('/');
    }
  };

  const scrollToServices = () => {
    if (location.pathname === '/') {
      setActiveSection('services');
      const servicesSection = document.getElementById('services-section');
      if (servicesSection) {
        servicesSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate('/');
      setTimeout(() => {
        const servicesSection = document.getElementById('services-section');
        if (servicesSection) {
          servicesSection.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  };

  React.useEffect(() => {
    if (location.pathname === '/about') {
      setActiveSection('about');
      window.scrollTo({ top: 0, behavior: 'instant' });
    } else if (location.pathname === '/') {
      if (location.hash === '#services') {
        setTimeout(() => {
          const servicesSection = document.getElementById('services-section');
          if (servicesSection) {
            servicesSection.scrollIntoView({ behavior: 'smooth' });
            setActiveSection('services');
          }
        }, 300);
      }

      const handleScroll = () => {
        const servicesSection = document.getElementById('services-section');
        if (servicesSection) {
          const rect = servicesSection.getBoundingClientRect();
          if (rect.top <= 200 && rect.bottom >= 200) {
            setActiveSection('services');
          } else if (window.scrollY < 400) {
            setActiveSection('home');
          }
        }
      };

      handleScroll();
      window.addEventListener('scroll', handleScroll);
      return () => window.removeEventListener('scroll', handleScroll);
    }
  }, [location.pathname, location.hash]);

  return (
    <>
      {location.pathname === '/' ? (
        <>
          <button
            onClick={scrollToHome}
            className={`relative px-4 py-2 text-sm font-semibold transition-all duration-300 ${
              activeSection === 'home'
                ? 'text-black'
                : 'text-black hover:text-neutral-700'
            } font-sans`}
          >
            {t('nav.home')}
            {activeSection === 'home' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: '#F8BD3D' }} />
            )}
          </button>
          <button
            onClick={scrollToServices}
            className={`relative px-4 py-2 text-sm font-semibold transition-all duration-300 ${
              activeSection === 'services'
                ? 'text-black'
                : 'text-black hover:text-neutral-700'
            } font-sans`}
          >
            {t('nav.services')}
            {activeSection === 'services' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: '#F8BD3D' }} />
            )}
          </button>
        </>
      ) : (
        <>
          <Link
            to="/"
            className="relative px-4 py-2 text-sm font-semibold transition-colors text-black hover:text-neutral-700 font-sans"
          >
            {t('nav.home')}
          </Link>
          <Link
            to="/#services"
            className="relative px-4 py-2 text-sm font-semibold transition-colors text-black hover:text-neutral-700 font-sans"
          >
            {t('nav.services')}
          </Link>
        </>
      )}
      <Link
        to="/about"
        className={`relative px-4 py-2 text-sm font-semibold transition-all duration-300 ${
          activeSection === 'about'
            ? 'text-black'
            : 'text-black hover:text-neutral-700'
        } font-sans`}
      >
        {t('nav.about')}
        {activeSection === 'about' && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5" style={{ backgroundColor: '#F8BD3D' }} />
        )}
      </Link>
    </>
  );
}

function MobileNavigation({ currentLanguage, handleLanguageChange }: { currentLanguage: string; handleLanguageChange: (lang: string) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();

  return (
    <div className="md:hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-white shadow-lg rounded-full p-2 border border-neutral-300 flex items-center justify-center"
      >
        {isOpen ? <X className="w-5 h-5 text-black" /> : <Menu className="w-5 h-5 text-black" />}
      </button>

      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setIsOpen(false)}>
          <div className="fixed top-0 right-0 h-full w-[85vw] max-w-[320px] bg-white shadow-xl transform transition-transform duration-300" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 pt-20">
              <div className="mb-6">
                <p className="text-xs text-neutral-500 mb-2 font-sans">Langue</p>
                <div className="flex space-x-2">
                  {[
                    { code: 'nl', label: 'NL' },
                    { code: 'fr', label: 'FR' },
                    { code: 'en', label: 'EN' }
                  ].map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`flex-1 px-4 py-2 rounded-full font-semibold transition-all duration-300 text-sm ${
                        currentLanguage === lang.code
                          ? 'shadow-md text-black'
                          : 'text-neutral-600'
                      }`}
                      style={{
                        backgroundColor: currentLanguage === lang.code ? '#F8BD3D' : 'rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              </div>
              <nav className="space-y-4">
                <Link
                  to="/about"
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    location.pathname === '/about'
                      ? 'bg-neutral-100 text-black'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  } font-sans`}
                >
                  {t('nav.about')}
                </Link>
                <Link
                  to="/nos-realisations"
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    location.pathname === '/nos-realisations'
                      ? 'bg-neutral-100 text-black'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  } font-sans`}
                >
                  Nos réalisations
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setIsOpen(false)}
                  className={`block px-4 py-3 rounded-lg font-medium transition-colors ${
                    location.pathname === '/contact'
                      ? 'bg-neutral-100 text-black'
                      : 'text-neutral-700 hover:bg-neutral-50'
                  } font-sans`}
                >
                  {t('header.contact')}
                </Link>
              </nav>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
